import type { ReactNode } from "react";
import { AlertTriangle, Lightbulb } from "lucide-react";

const calloutStyles = {
  note: { box: "bg-warmBeige-100 border border-warmBeige-300", Icon: Lightbulb },
  warning: { box: "bg-warmOrange-50 border border-warmOrange-200", Icon: AlertTriangle },
} as const;

type CalloutType = keyof typeof calloutStyles;

const isCalloutType = (value: unknown): value is CalloutType =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(calloutStyles, value);

/** `<Callout type="note|warning" title="...">`: UI-SPEC section 4. Unknown type falls back to note. */
export function Callout({
  type,
  title,
  children,
}: {
  type?: string;
  title?: string;
  children?: ReactNode;
}) {
  const { box, Icon } = calloutStyles[isCalloutType(type) ? type : "note"];

  return (
    <div role="note" className={`my-6 rounded-lg p-4 flex gap-4 items-start ${box}`}>
      <Icon className="h-5 w-5 text-warmOrange-700 shrink-0 mt-1" aria-hidden="true" />
      <div className="min-w-0">
        {title ? (
          <p className="text-base leading-[1.8] font-bold text-gray-900">{title}</p>
        ) : null}
        <div className="text-base leading-[1.8] text-gray-700 [&>p:last-child]:mb-0">
          {children}
        </div>
      </div>
    </div>
  );
}

export default Callout;
